import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PG } from '../../utils/theme';
import { useLiveFeed } from '../../components/live/LiveFeedProvider';
import {
  GROUP_MEASURE,
  BRANCH_MEASURE,
  COUNTRY_MEASURE,
  REGION_MEASURE,
  TOWN_MEASURE,
  TOP_NATIONAL,
  TOP_MOVERS,
  TOP_STREAKS,
  BRANCH_BREACHES,
  ACHIEVEMENT_FEED,
  STAFF,
  BADGES,
} from '../../utils/measurabilityData';

const {
  FiTarget,
  FiCrosshair,
  FiClock,
  FiFileText,
  FiAward,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
  FiZap,
  FiChevronRight,
} = FiIcons;

const tileColor = (score) => (score >= 90 ? PG.emerald : score >= 80 ? PG.amber : PG.red);
const trendArrow = (d) =>
  d > 0
    ? { icon: FiTrendingUp, color: PG.emerald }
    : d < 0
    ? { icon: FiTrendingDown, color: PG.red }
    : { icon: FiMinus, color: PG.neutral500 };

const MEDAL_COLORS = ['#FFB800', '#C0C0C0', '#CD7F32'];

export default function MeasurabilitySliver() {
  useLiveFeed();

  const [selectedCountry, setSelectedCountry] = useState('South Africa');
  const [selectedRegion, setSelectedRegion] = useState('Gauteng');

  const wallPeople = useMemo(() => {
    const topIds = new Set(TOP_NATIONAL.map((p) => p.id));
    const rest = [...STAFF]
      .filter((p) => !topIds.has(p.id))
      .sort((a, b) => b.score - a.score)
      .slice(0, 28);
    return [...TOP_NATIONAL, ...rest].slice(0, 40);
  }, []);

  const podium = TOP_NATIONAL.slice(0, 3);
  const challengers = TOP_NATIONAL.slice(3, 10);

  const visibleRegions = useMemo(
    () => REGION_MEASURE.filter((r) => r.country === selectedCountry),
    [selectedCountry]
  );

  const visibleTowns = useMemo(
    () => TOWN_MEASURE.filter((t) => t.country === selectedCountry && t.region === selectedRegion),
    [selectedCountry, selectedRegion]
  );

  const firstBranchInTown = (town) => {
    const b = Object.values(BRANCH_MEASURE).find(
      (br) => br.town === town.name && br.region === town.region && br.country === town.country
    );
    return b?.id;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard
          title="GROUP SCORE"
          value={GROUP_MEASURE.score}
          unit="/100"
          icon={FiTarget}
          accent={PG.emerald}
          target=">85"
          animated="ticker"
          numericValue={Math.round(GROUP_MEASURE.score * 10)}
          tickerDigits={4}
          tickerSuffix="/1000"
        />
        <KPICard
          title="ON TARGET"
          value={GROUP_MEASURE.onTargetPct}
          unit="%"
          icon={FiCrosshair}
          accent={PG.amber}
          target=">70%"
        />
        <KPICard
          title="SLA AVG"
          value={GROUP_MEASURE.slaAvg}
          unit="%"
          icon={FiClock}
          accent={PG.cyan}
          target=">95%"
        />
        <KPICard
          title="REPORT ON TIME"
          value={GROUP_MEASURE.reportAvg}
          unit="%"
          icon={FiFileText}
          accent={PG.emerald}
          target="100%"
        />
        <KPICard
          title="ACTIVE BADGES"
          icon={FiAward}
          accent={PG.amber}
          animated="splitflap"
          flapText={String(GROUP_MEASURE.badgeCount).padStart(3, '0')}
          flapWidth={3}
        />
      </div>

      <div className="grid grid-cols-12 grid-rows-2 gap-4 flex-1 min-h-0">
        <Panel title="NATIONAL_TOP_10" subtitle="podium + challengers" className="col-span-3">
          <div className="h-full overflow-y-auto custom-scrollbar p-3 flex flex-col gap-3">
            <div className="flex flex-col gap-3">
              {podium.map((p, i) => {
                const medal = MEDAL_COLORS[i];
                return (
                  <Link
                    key={p.id}
                    to={`/sliver/measurability/branch/${p.branchId}`}
                    className="flex items-center gap-3 bg-black/30 rounded-md p-2 hover:bg-[#D71921]/10 transition-colors"
                  >
                    <div className="relative flex-shrink-0">
                      <div
                        className="rounded-full overflow-hidden"
                        style={{
                          width: 72,
                          height: 72,
                          border: `3px solid ${medal}`,
                          boxShadow: `0 0 12px ${medal}40`,
                        }}
                      >
                        <img
                          src={p.portraitUrl}
                          alt={p.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                      <div
                        className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center font-doto text-sm text-black font-bold"
                        style={{ backgroundColor: medal }}
                      >
                        {i + 1}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{p.name}</div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 truncate">
                        {p.role}
                      </div>
                      <div className="font-doto text-2xl text-white leading-none mt-1">
                        {p.score}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="border-t border-white/5 pt-2 flex flex-col gap-1">
              <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 px-1 pb-1">
                Challengers 4—10
              </div>
              {challengers.map((p, idx) => {
                const t = trendArrow(p.deltaWeek);
                const TrendIcon = t.icon;
                return (
                  <Link
                    key={p.id}
                    to={`/sliver/measurability/branch/${p.branchId}`}
                    className="flex items-center gap-2 px-1 py-1.5 hover:bg-[#D71921]/10 rounded transition-colors"
                  >
                    <div
                      className="font-mono-tech text-[10px] text-neutral-500 w-5 text-right"
                    >
                      {idx + 4}
                    </div>
                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border border-white/10">
                      <img
                        src={p.portraitUrl}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-white truncate">{p.name}</div>
                    </div>
                    <div className="font-doto text-sm text-white leading-none">{p.score}</div>
                    <div
                      className="flex items-center gap-0.5 text-[10px] font-mono-tech"
                      style={{ color: t.color }}
                    >
                      <TrendIcon size={10} />
                      {p.deltaWeek === 0 ? '—' : Math.abs(p.deltaWeek)}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </Panel>

        <Panel
          title="WALL_OF_FAME"
          subtitle={`${STAFF.length} people · live status`}
          className="col-span-6"
        >
          <div className="h-full overflow-y-auto custom-scrollbar p-2">
            <div className="grid grid-cols-6 xl:grid-cols-8 gap-2">
              {wallPeople.map((p) => {
                const color = tileColor(p.score);
                const firstBadge = p.badges?.[0] ? BADGES[p.badges[0]] : null;
                return (
                  <Link
                    key={p.id}
                    to={`/sliver/measurability/branch/${p.branchId}`}
                    className="relative block aspect-square group overflow-hidden rounded hover:scale-105 transition-transform"
                    style={{
                      border: `2px solid ${color}`,
                      boxShadow: 'inset 0 -20px 0 rgba(0,0,0,0.5)',
                    }}
                  >
                    <img
                      src={p.portraitUrl}
                      alt={p.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />

                    {firstBadge && (
                      <div
                        className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center font-bold text-[9px] text-black rounded-sm"
                        style={{ backgroundColor: firstBadge.color }}
                      >
                        {firstBadge.glyph}
                      </div>
                    )}

                    <div
                      className="absolute inset-x-0 bottom-0 px-1.5 py-1"
                      style={{
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0) 100%)',
                      }}
                    >
                      <div className="text-[10px] font-medium text-white truncate leading-tight">
                        {p.name}
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <div className="font-doto text-sm text-white leading-none">{p.score}</div>
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: color, boxShadow: `0 0 4px ${color}` }}
                        />
                      </div>
                    </div>

                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/85 flex flex-col justify-center items-center px-2 text-center pointer-events-none">
                      <div className="text-[11px] font-medium text-white leading-tight">
                        {p.name}
                      </div>
                      <div className="font-doto text-xl text-white leading-none mt-1">
                        {p.score}
                      </div>
                      <div className="font-mono-tech text-[8px] uppercase tracking-wider text-neutral-400 mt-1 leading-tight">
                        {p.role}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </Panel>

        <Panel
          title="LIVE_ACHIEVEMENTS"
          subtitle="last 3 hours"
          className="col-span-3"
          livePulse
        >
          <div className="h-full overflow-hidden relative">
            <motion.div
              animate={{ y: [0, '-50%'] }}
              transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
              className="flex flex-col"
            >
              {[...ACHIEVEMENT_FEED, ...ACHIEVEMENT_FEED].map((item, i) => (
                <div
                  key={`${item.id}-${i}`}
                  className="flex items-stretch gap-2 px-2 py-2 border-b border-white/5"
                >
                  <div
                    className="w-0.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: item.badgeColor }}
                  />
                  <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 border border-white/10">
                    <img
                      src={item.portraitUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-neutral-300 leading-tight">{item.text}</div>
                    <div className="text-[9px] font-mono-tech uppercase text-neutral-500 mt-0.5">
                      {item.minutesAgo}m ago
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </Panel>

        <Panel title="HIERARCHY_EXPLORER" subtitle="drill by geography" className="col-span-5">
          <div className="grid grid-cols-3 gap-2 h-full overflow-hidden p-2">
            <div className="flex flex-col min-h-0 border border-white/5 rounded">
              <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 px-2 py-1.5 border-b border-white/5 bg-black/30">
                Countries
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {COUNTRY_MEASURE.map((c) => {
                  const active = c.name === selectedCountry;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSelectedCountry(c.name)}
                      className={`w-full text-left px-2 py-2 border-b border-white/5 hover:bg-[#D71921]/10 transition-colors ${
                        active ? 'bg-[#D71921]/15' : ''
                      }`}
                      style={active ? { borderLeft: `2px solid ${PG.red}` } : undefined}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <div className="text-[11px] text-white truncate">{c.name}</div>
                        <FiChevronRight size={10} className="text-neutral-500 flex-shrink-0" />
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="font-doto text-sm text-white leading-none">{c.score}</div>
                        <div className="font-mono-tech text-[9px] text-neutral-400">
                          {c.onTargetPct}% · {c.badgeCount}b
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col min-h-0 border border-white/5 rounded">
              <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 px-2 py-1.5 border-b border-white/5 bg-black/30">
                Regions
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {visibleRegions.map((r) => {
                  const active = r.name === selectedRegion;
                  return (
                    <button
                      key={r.id}
                      onClick={() => setSelectedRegion(r.name)}
                      className={`w-full text-left px-2 py-2 border-b border-white/5 hover:bg-[#D71921]/10 transition-colors ${
                        active ? 'bg-[#D71921]/15' : ''
                      }`}
                      style={active ? { borderLeft: `2px solid ${PG.red}` } : undefined}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <div className="text-[11px] text-white truncate">{r.name}</div>
                        <FiChevronRight size={10} className="text-neutral-500 flex-shrink-0" />
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="font-doto text-sm text-white leading-none">{r.score}</div>
                        <div className="font-mono-tech text-[9px] text-neutral-400">
                          {r.onTargetPct}% · {r.badgeCount}b
                        </div>
                      </div>
                    </button>
                  );
                })}
                {visibleRegions.length === 0 && (
                  <div className="text-[10px] text-neutral-500 p-2">No regions.</div>
                )}
              </div>
            </div>

            <div className="flex flex-col min-h-0 border border-white/5 rounded">
              <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 px-2 py-1.5 border-b border-white/5 bg-black/30">
                Towns
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {visibleTowns.map((t) => {
                  const branchId = firstBranchInTown(t);
                  const content = (
                    <>
                      <div className="flex items-center justify-between gap-1">
                        <div className="text-[11px] text-white truncate">{t.name}</div>
                        <FiChevronRight size={10} className="text-neutral-500 flex-shrink-0" />
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="font-doto text-sm text-white leading-none">{t.score}</div>
                        <div className="font-mono-tech text-[9px] text-neutral-400">
                          {t.onTargetPct}% · {t.badgeCount}b
                        </div>
                      </div>
                    </>
                  );
                  return branchId ? (
                    <Link
                      key={t.id}
                      to={`/sliver/measurability/branch/${branchId}`}
                      className="block text-left px-2 py-2 border-b border-white/5 hover:bg-[#D71921]/10 transition-colors"
                    >
                      {content}
                    </Link>
                  ) : (
                    <div
                      key={t.id}
                      className="text-left px-2 py-2 border-b border-white/5 opacity-60"
                    >
                      {content}
                    </div>
                  );
                })}
                {visibleTowns.length === 0 && (
                  <div className="text-[10px] text-neutral-500 p-2">No towns.</div>
                )}
              </div>
            </div>
          </div>
        </Panel>

        <Panel title="MOVERS_AND_STREAKS" subtitle="this week" className="col-span-4">
          <div className="h-full flex flex-col p-2 gap-2 overflow-hidden">
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 px-1 pb-1 flex items-center gap-1">
                <FiTrendingUp size={10} style={{ color: PG.emerald }} /> Rising
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {TOP_MOVERS.filter((p) => p.deltaWeek > 0).map((p) => (
                  <Link
                    key={p.id}
                    to={`/sliver/measurability/branch/${p.branchId}`}
                    className="flex items-center gap-2 px-1 py-1.5 hover:bg-[#D71921]/10 rounded transition-colors"
                  >
                    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border border-white/10">
                      <img
                        src={p.portraitUrl}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-white truncate">{p.name}</div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-wider text-neutral-500 truncate">
                        {p.townName}
                      </div>
                    </div>
                    <StatusPill tone="ok">
                      <span className="flex items-center gap-0.5">
                        <FiTrendingUp size={9} />
                        {p.deltaWeek}
                      </span>
                    </StatusPill>
                  </Link>
                ))}
              </div>
            </div>

            <div className="border-t border-white/5 pt-2 flex-1 min-h-0 flex flex-col">
              <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 px-1 pb-1 flex items-center gap-1">
                <FiZap size={10} style={{ color: PG.amber }} /> Longest Streaks
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                {TOP_STREAKS.map((p) => (
                  <Link
                    key={p.id}
                    to={`/sliver/measurability/branch/${p.branchId}`}
                    className="flex items-center gap-2 px-1 py-1.5 hover:bg-[#D71921]/10 rounded transition-colors"
                  >
                    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border border-white/10">
                      <img
                        src={p.portraitUrl}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-white truncate">{p.name}</div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-wider text-neutral-500 truncate">
                        {p.townName}
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-1 font-mono-tech text-[10px]"
                      style={{ color: PG.amber }}
                    >
                      <span className="text-base leading-none">♨</span>
                      {p.streakDays}d
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </Panel>

        <Panel
          title="WALL_OF_SHAME"
          subtitle="branch-level · anonymised"
          className="col-span-3"
          tone="alert"
        >
          <div className="h-full flex flex-col overflow-hidden">
            <div className="font-mono-tech text-[9px] uppercase tracking-wider text-neutral-500 px-3 py-2 border-b border-white/5">
              No faces. Branches carry the accountability.
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {BRANCH_BREACHES.map((b) => {
                const tone = b.breachCount >= 3 ? 'alert' : 'warn';
                return (
                  <Link
                    key={b.id}
                    to={`/sliver/measurability/branch/${b.id}`}
                    className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/5 hover:bg-[#D71921]/10 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-white truncate font-medium">{b.name}</div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-wider text-neutral-500 truncate">
                        {b.town} · {b.region}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <StatusPill tone={tone}>{b.breachCount} breach</StatusPill>
                      <div className="font-doto text-sm text-white leading-none">
                        {b.slaAvg}
                        <span className="text-[9px] text-neutral-500 ml-0.5">SLA</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
